import type {UniqueIdentifier} from '@dnd-kit/core/dist/types/other';
import type {SxProps} from '@mui/material';
import type {MetadataId} from 'types';
import {Concept} from 'types';
import type {LoadStatus} from 'api/types';
import type {IconButtonProps} from 'components/button/types';
import type {Schema} from 'schemaDefinition/types';
import type {
  AvailableFilterWithLabel,
  ColumnSet,
  ColumnWithLabel,
  CreateFilterSet,
  CurrentFilterType,
  CustomFilterType,
  DataResponse,
  Filter,
  FilterSet,
  PostInfoArgs,
  ViewMode,
} from 'services/advancedSearch/types';
import type {ThesaurusNode} from 'services/thesaurus/types';

export type AdvancedSearchContextType = CurrentFiltersType &
  ViewModeType &
  Omit<CurrentColumnsType, 'loadingColumns'> &
  Pick<DataFetchType, 'dataResponse' | 'loadingSearch' | 'loadingDownload'> &
  Pick<AvailableFiltersType, 'availableFilters'> & {
    loading: boolean;
    onSearch: (resetPage?: boolean) => void;
    onDownload: (
      viewMode: ViewMode,
      columns?: string[],
      distinctOn?: string,
    ) => Promise<void>;
  } & {
    getMetadataIdsPage: (page: number, size: number) => Promise<MetadataId[]>;
    getCurrentMetadataIds: () => MetadataId[];
  };

export type ViewModeType = {
  viewMode: ViewMode;
  setViewMode: (value: ViewMode) => void;
};

export type DataFetchType = {
  dataResponse: DataResponse;
  onSearch: (args: PostInfoArgs) => void;
  onDownload: (
    type: ViewMode,
    filters: Filter[],
    columns: string[] | undefined,
    distinctOn?: string,
  ) => Promise<void>;
  loadingSearch: boolean;
  loadingDownload: boolean;
};

export type CurrentFiltersType = {
  currentFilters: CurrentFilterType[];
  setCurrentFilters: (filters: Array<Filter | CustomFilterType>) => void;
  addCurrentFilter: (filter: Filter) => void;
  updateCurrentFilter: (id: number, filter: Filter) => void;
  removeCurrentFilterById: (id: number) => void;
};

export type CurrentColumnsType = {
  schema: Schema;
  allColumns: ColumnWithLabel[];
  loadingColumns: boolean;
  currentColumns: string[];
  addCurrentColumn: (column: string) => void;
  setCurrentColumns: (columns: string[]) => void;
  removeCurrentColumn: (column: string) => void;
};

export type AvailableFiltersType = {
  availableFilters: AvailableFilterWithLabel[];
  statusAvailableFilters: LoadStatus;
};

export type FilterSetsType = {
  filterSets: FilterSet[];
  statusFilterSets: LoadStatus;
  createFilterSet: (filterSet: CreateFilterSet) => Promise<void>;
  deleteFilterSet: (filterSet: Pick<FilterSet, 'id'>) => Promise<void>;
};

export type ColumnSetsType = {
  columnSets: ColumnSet[];
  statusColumnSets: LoadStatus;
  createColumnSet: (columnSet: Omit<ColumnSet, 'id'>) => Promise<void>;
  editColumnSet: (columnSet: ColumnSet) => Promise<void>;
  deleteColumnSet: (columnSet: Pick<ColumnSet, 'id'>) => Promise<void>;
};

export const ViewModes: ViewMode[] = [Concept.manifestation, Concept.work];

export type BooleanOption<T = 'true' | 'false'> = {
  value: T;
  label: string;
};
export type BooleanOptions = [BooleanOption<'true'>, BooleanOption<'false'>];

export const advancedSearchSchemaName = 'advancedSearch';

export type SortDirection = 'asc' | 'desc';

export type AutocompleteOption = {
  id: string;
  label: string;
};

export type NodeMapper = (node: ThesaurusNode) => AutocompleteOption;

export type ColumnName = 'pickableColumns' | 'pickedColumns';
export const ColumnNames: ColumnName[] = ['pickableColumns', 'pickedColumns'];
export type ColumnItems = {
  [key in ColumnName]: Array<string>;
};

export type DndDraggableItem = {
  id: UniqueIdentifier;
  label: string;
};

export type DndItemProps = DndDraggableItem & {
  active?: boolean;
  action?: DndItemAction;
};

export type DndItemAction = {
  type: 'add' | 'remove';
  action: (item: DndDraggableItem) => void;
};

export type ItemAction = {
  position: 'beforeText' | 'afterText';
  props: IconButtonProps;
};

export type ItemProps = {
  label: string;
  onClick?: () => void;
  active?: boolean;
  actions?: ItemAction[];
  sxProps?: SxProps;
  disabled?: boolean;
};
